import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { CalendarPlus, Plus, Trash2, Users, CalendarDays, Repeat } from 'lucide-react';
import {
  getAcademySessies,
  maakAcademySessie,
  wijzigAcademySessieCapaciteit,
  verwijderAcademySessie,
} from '../services/opleidingenApi';
import type { OpleiderAcademySessie } from '../services/opleidingenApi';

interface LesInvoer {
  datum: string;
  van: string;
  tot: string;
  locatie: string;
}

const LEGE_LES: LesInvoer = { datum: '', van: '19:00', tot: '21:30', locatie: '' };

function foutMelding(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const msg = (error.response?.data as { message?: string } | undefined)?.message;
    if (msg) return msg;
  }
  return error instanceof Error ? error.message : fallback;
}

function datumPlus7(datum: string): string {
  const d = new Date(`${datum}T12:00:00`);
  d.setDate(d.getDate() + 7);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const lesTijd = (iso: string) =>
  new Date(iso).toLocaleString('nl-NL', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const STATUS_STIJL: Record<OpleiderAcademySessie['status'], { cls: string; label: string }> = {
  AVAILABLE: { cls: 'bg-green-50 text-green-800 border-green-200', label: 'Boekbaar' },
  LOCKED_FULL: { cls: 'bg-red-50 text-red-700 border-red-200', label: 'Vol' },
  EXPIRED: { cls: 'bg-gray-100 text-gray-600 border-gray-200', label: 'Verlopen' },
};

/**
 * Academy-startmomenten van een live workshop/opleiding: per sessie de lesdata
 * (les 1 = startdatum) en het maximaal aantal deelnemers. Een nieuwe startdatum
 * zet een volle/verlopen cursus direct weer live in de medewerker-swipers.
 */
export const OpleiderAcademySessies: React.FC<{ trainingId: number }> = ({ trainingId }) => {
  const { t } = useTranslation('pages');
  const queryClient = useQueryClient();
  const queryKey = ['academy-sessies', trainingId];
  const sessiesQuery = useQuery({ queryKey, queryFn: () => getAcademySessies(trainingId) });

  const [naam, setNaam] = useState('');
  const [max, setMax] = useState('15');
  const [lessen, setLessen] = useState<LesInvoer[]>([{ ...LEGE_LES }]);
  const [fout, setFout] = useState<string | null>(null);

  const ververs = () => queryClient.invalidateQueries({ queryKey });

  const maakMutation = useMutation({
    mutationFn: () => {
      const schoon = lessen.filter((l) => l.datum);
      if (schoon.length === 0) throw new Error(t('opleider.sessieGeenLes', { defaultValue: 'Voeg minstens één lesdatum toe.' }));
      return maakAcademySessie(trainingId, {
        naam: naam.trim() || null,
        max_deelnemers: parseInt(max, 10),
        lessen: schoon.map((l) => ({
          start_at: new Date(`${l.datum}T${l.van}`).toISOString(),
          eind_at: new Date(`${l.datum}T${l.tot}`).toISOString(),
          locatie: l.locatie.trim() || null,
        })),
      });
    },
    onSuccess: () => {
      setNaam('');
      setLessen([{ ...LEGE_LES }]);
      setFout(null);
      ververs();
    },
    onError: (e) => setFout(foutMelding(e, 'Opslaan mislukt.')),
  });

  const capaciteitMutation = useMutation({
    mutationFn: ({ sessieId, waarde }: { sessieId: number; waarde: number }) => wijzigAcademySessieCapaciteit(trainingId, sessieId, waarde),
    onSuccess: () => {
      setFout(null);
      ververs();
    },
    onError: (e) => setFout(foutMelding(e, 'Capaciteit wijzigen mislukt.')),
  });

  const verwijderMutation = useMutation({
    mutationFn: (sessieId: number) => verwijderAcademySessie(trainingId, sessieId),
    onSuccess: () => {
      setFout(null);
      ververs();
    },
    onError: (e) => setFout(foutMelding(e, 'Verwijderen mislukt.')),
  });

  const zetLes = (i: number, veld: keyof LesInvoer, waarde: string) =>
    setLessen((huidig) => huidig.map((l, j) => (j === i ? { ...l, [veld]: waarde } : l)));

  const voegWekelijksToe = () =>
    setLessen((huidig) => {
      const laatste = huidig[huidig.length - 1];
      return [...huidig, laatste?.datum ? { ...laatste, datum: datumPlus7(laatste.datum) } : { ...LEGE_LES }];
    });

  const sessies = sessiesQuery.data ?? [];

  return (
    <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-4">
      <div>
        <h2 className="font-semibold text-gray-900 flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-[#0B1F3A]" aria-hidden="true" />
          {t('opleider.sessiesTitel', { defaultValue: 'Academy — startdata & capaciteit' })}
        </h2>
        <p className="text-xs text-gray-500 mt-1">
          {t('opleider.sessiesUitleg', {
            defaultValue:
              'Voor live workshops en opleidingen. Medewerkers kiezen alleen de startdatum; de vervolgdata zien ze als tijdlijn en krijgen ze als agenda-bestand. Is een datum vol of verlopen, dan verdwijnt de cursus uit de rijen tot u een nieuwe startdatum toevoegt. Zonder startdata is de cursus direct te boeken (bijv. e-learning).',
          })}
        </p>
      </div>

      {fout && (
        <p className="text-sm text-red-600" role="alert">
          {fout}
        </p>
      )}

      {sessiesQuery.isLoading && <p className="text-sm text-gray-500">…</p>}
      {sessies.length > 0 && (
        <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
          {sessies.map((s) => {
            const stijl = STATUS_STIJL[s.status];
            return (
              <li key={s.id} className="p-3 flex flex-wrap items-start gap-3 justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    {s.naam || t('opleider.sessieZonderNaam', { defaultValue: 'Sessie' })}
                    <span className={`ml-2 inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium ${stijl.cls}`}>{stijl.label}</span>
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    {s.start_at ? lesTijd(s.start_at) : '—'} · {s.lessen.length}{' '}
                    {s.lessen.length === 1 ? t('opleider.les', { defaultValue: 'les' }) : t('opleider.lessen', { defaultValue: 'lessen' })}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 text-xs text-gray-700">
                    <Users className="w-3.5 h-3.5" aria-hidden="true" />
                    {s.geboekt} /
                  </span>
                  <input
                    type="number"
                    min={Math.max(1, s.geboekt)}
                    max={1000}
                    defaultValue={s.max_deelnemers}
                    disabled={s.status === 'EXPIRED'}
                    aria-label={t('opleider.sessieMax', { defaultValue: 'Maximaal aantal deelnemers' })}
                    className="w-20 border border-gray-300 rounded-md px-2 py-1 text-sm"
                    onBlur={(e) => {
                      const waarde = parseInt(e.target.value, 10);
                      if (Number.isFinite(waarde) && waarde !== s.max_deelnemers) capaciteitMutation.mutate({ sessieId: s.id, waarde });
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => verwijderMutation.mutate(s.id)}
                    disabled={s.geboekt > 0 || verwijderMutation.isPending}
                    title={s.geboekt > 0 ? t('opleider.sessieHeeftBoekingen', { defaultValue: 'Heeft al boekingen' }) : undefined}
                    className="p-1.5 rounded-md text-gray-500 hover:text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-500"
                    aria-label={t('opleider.sessieVerwijderen', { defaultValue: 'Startdatum verwijderen' })}
                  >
                    <Trash2 className="w-4 h-4" aria-hidden="true" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="rounded-lg border border-dashed border-gray-300 p-4 space-y-3">
        <p className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
          <CalendarPlus className="w-4 h-4 text-[#0B1F3A]" aria-hidden="true" />
          {t('opleider.sessieNieuw', { defaultValue: 'Nieuwe startdatum' })}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('opleider.sessieNaam', { defaultValue: 'Naam (optioneel)' })}</label>
            <input
              type="text"
              maxLength={80}
              value={naam}
              onChange={(e) => setNaam(e.target.value)}
              placeholder={t('opleider.sessieNaamPh', { defaultValue: 'bijv. Sessie Najaar' })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('opleider.sessieMax', { defaultValue: 'Maximaal aantal deelnemers' })}</label>
            <input type="number" min={1} max={1000} value={max} onChange={(e) => setMax(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
        </div>

        <div className="space-y-2">
          {lessen.map((l, i) => (
            <div key={i} className="grid grid-cols-2 sm:grid-cols-[auto_1fr_auto_auto_1.5fr_auto] items-end gap-2">
              <span className="col-span-2 sm:col-span-1 text-xs font-medium text-gray-600 sm:pb-2.5">
                {i === 0 ? t('opleider.lesStart', { defaultValue: 'Les 1 (start)' }) : t('opleider.lesN', { defaultValue: 'Les {{n}}', n: i + 1 })}
              </span>
              <input
                type="date"
                value={l.datum}
                onChange={(e) => zetLes(i, 'datum', e.target.value)}
                aria-label={t('opleider.lesDatum', { defaultValue: 'Datum' })}
                className="col-span-2 sm:col-span-1 border border-gray-300 rounded-lg px-2 py-2 text-sm"
              />
              <input type="time" value={l.van} onChange={(e) => zetLes(i, 'van', e.target.value)} aria-label={t('opleider.lesVan', { defaultValue: 'Van' })} className="border border-gray-300 rounded-lg px-2 py-2 text-sm" />
              <input type="time" value={l.tot} onChange={(e) => zetLes(i, 'tot', e.target.value)} aria-label={t('opleider.lesTot', { defaultValue: 'Tot' })} className="border border-gray-300 rounded-lg px-2 py-2 text-sm" />
              <input
                type="text"
                value={l.locatie}
                onChange={(e) => zetLes(i, 'locatie', e.target.value)}
                placeholder={t('opleider.lesLocatie', { defaultValue: 'Locatie (optioneel)' })}
                className="col-span-2 sm:col-span-1 border border-gray-300 rounded-lg px-2 py-2 text-sm"
              />
              <button
                type="button"
                onClick={() => setLessen((huidig) => huidig.filter((_, j) => j !== i))}
                disabled={lessen.length === 1}
                className="p-2 rounded-md text-gray-500 hover:text-red-600 disabled:opacity-30"
                aria-label={t('opleider.lesVerwijderen', { defaultValue: 'Les verwijderen' })}
              >
                <Trash2 className="w-4 h-4" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={voegWekelijksToe}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <Repeat className="w-4 h-4" aria-hidden="true" />
            {t('opleider.lesWekelijks', { defaultValue: 'Les toevoegen (+1 week)' })}
          </button>
          <button
            type="button"
            onClick={() => setLessen((huidig) => [...huidig, { ...LEGE_LES }])}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <Plus className="w-4 h-4" aria-hidden="true" />
            {t('opleider.lesLeeg', { defaultValue: 'Losse les toevoegen' })}
          </button>
          <button
            type="button"
            onClick={() => maakMutation.mutate()}
            disabled={maakMutation.isPending}
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-[#0B1F3A] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1A3459] disabled:opacity-60"
          >
            <CalendarPlus className="w-4 h-4" aria-hidden="true" />
            {t('opleider.sessieOpslaan', { defaultValue: 'Startdatum publiceren' })}
          </button>
        </div>
      </div>
    </section>
  );
};

export default OpleiderAcademySessies;
